import {
  IAutomationActionTemplateHttpPost,
  IAutomationTemplate
} from '@dadohr/shared/domainTypes/experience';
import { MenuItem, TextField, Typography } from '@material-ui/core';
import { SetStateAction, useEffect, useState } from 'react';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { TokenDefinition } from '../../../../../../../components/MarkdownText';
import { SelectCustom } from '../../../../../../../components/SelectCustom';
import { TextFieldWithReplacementTokens } from '../../../../../../../components/TextFieldWithReplacementTokens';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../../services/currentUser';
import {
  MASK,
  masked
} from '../../../../../../Org/pages/Integrations/integrations/common';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  useFieldValidator,
  VALIDATORS_HTTP_POST
} from '../../validation';

export const ActionSettingsHttpPost = ({
  value,
  onChange,
  expTpl,
  aTpl,
  validation,
  setValidation,
  tokens
}: {
  value: IAutomationActionTemplateHttpPost;
  onChange: (nextValue: IAutomationActionTemplateHttpPost) => void;
  expTpl: TaskBuilderContext;
  aTpl: IAutomationTemplate;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();

  console.log('VALUE', value);

  const validateRequestUrl = useFieldValidator(
    setValidation,
    VALIDATORS_HTTP_POST,
    'requestUrl'
  );

  const invalidRequestUrl = validation?.fields.requestUrl || null;
  //   const ivalidRequestBody  = validation?.fields.jsonPayload || null;

  const getAuthState = (
    authValue: IAutomationActionTemplateHttpPost['auth']
  ): IAutomationActionTemplateHttpPost['auth'] => {
    switch (authValue.type) {
      case 'BASIC_AUTH':
        return {
          ...authValue,
          password: masked(authValue.password)
        };
      case 'HEADER':
        return {
          ...authValue,
          value: masked(authValue.value)
        };
      case 'NO_AUTH':
      default:
        return authValue;
    }
  };

  const [authState, setAuthState] = useState<
    IAutomationActionTemplateHttpPost['auth']
  >(getAuthState(value.auth));

  useEffect(() => {
    setAuthState(getAuthState(value.auth));
  }, [value.auth]);

  return (
    <FlexContainerVertical spacing={3} fullWidth>
      <FlexContainerVertical spacing={1} fullWidth>
        <Typography variant="h6">{t('URL')}</Typography>
        <TextFieldWithReplacementTokens
          fullWidth
          tokens={tokens}
          placeholder={t('Request URL')}
          error={!!invalidRequestUrl}
          helperText={invalidRequestUrl || ''}
          value={value.requestUrl}
          onChange={(nextUrl) => {
            const nextValue: IAutomationActionTemplateHttpPost = {
              ...value,
              requestUrl: nextUrl
            };
            validateRequestUrl(nextValue, expTpl, aTpl);
            onChange(nextValue);
          }}
        />
      </FlexContainerVertical>
      <FlexContainerVertical>
        <Typography variant="h6">{t('Authentication')}</Typography>
        <SelectCustom
          label={t('Authentication Type')}
          value={value.auth.type}
          onChange={(ev) => {
            const nextauthType = ev.target.value as string;
            const getEmptyAuth = (
              authType: IAutomationActionTemplateHttpPost['auth']['type']
            ): IAutomationActionTemplateHttpPost['auth'] => {
              switch (authType) {
                case 'BASIC_AUTH':
                  return { type: 'BASIC_AUTH', username: '', password: '' };
                case 'HEADER':
                  return { type: 'HEADER', value: '' };

                case 'NO_AUTH':
                default:
                  return { type: 'NO_AUTH' };
              }
            };
            const nextValue: IAutomationActionTemplateHttpPost = {
              ...value,
              auth: getEmptyAuth(
                nextauthType as IAutomationActionTemplateHttpPost['auth']['type']
              )
            };
            onChange(nextValue);
          }}
          renderInitialValueImmediately
          fullWidth
        >
          {[
            { value: 'BASIC_AUTH', key: 'Basic Authentication' },
            { value: 'HEADER', key: 'Authorization Header' },
            { value: 'NO_AUTH', key: 'None' }
          ].map((o) => (
            <MenuItem key={o.key} value={o.value}>
              {o.key}
            </MenuItem>
          ))}
        </SelectCustom>

        {authState.type === 'HEADER' && value.auth.type === 'HEADER' && (
          <TextField
            label={t('Header')}
            variant="outlined"
            value={authState.value}
            onChange={(ev) => {
              const nextValue: IAutomationActionTemplateHttpPost['auth'] = {
                type: 'HEADER',
                value: ev.target.value || ''
              };
              setAuthState(nextValue);
            }}
            onBlur={(ev) => {
              const nextValue = ev.target.value || '';
              if (nextValue !== MASK) {
                const nextValue: IAutomationActionTemplateHttpPost = {
                  ...value,
                  auth: {
                    type: 'HEADER',
                    value: ev.target.value || ''
                  }
                };
                onChange(nextValue);
              }
            }}
            fullWidth
          />
        )}

        {authState.type === 'BASIC_AUTH' && value.auth.type === 'BASIC_AUTH' && (
          <>
            <TextField
              label={t('Username')}
              variant="outlined"
              value={authState.username}
              onChange={(ev) => {
                /// For some reason the check above does not work
                if (value.auth.type !== 'BASIC_AUTH') {
                  return;
                }
                const nextValue: IAutomationActionTemplateHttpPost = {
                  ...value,
                  auth: {
                    ...value.auth,
                    username: ev.target.value || ''
                  }
                };
                onChange(nextValue);
              }}
              fullWidth
            />
            <TextField
              label={t('Password')}
              variant="outlined"
              value={authState.password}
              onChange={(ev) => {
                const nextValue: IAutomationActionTemplateHttpPost['auth'] = {
                  ...authState,
                  password: ev.target.value || ''
                };
                setAuthState(nextValue);
              }}
              onBlur={(ev) => {
                const nextValue = ev.target.value || '';
                if (nextValue !== MASK) {
                  const nextValue: IAutomationActionTemplateHttpPost = {
                    ...value,
                    auth: {
                      ...authState,
                      password: ev.target.value || ''
                    }
                  };
                  onChange(nextValue);
                }
              }}
              fullWidth
            />
          </>
        )}
      </FlexContainerVertical>

      <FlexContainerVertical>
        <Typography variant="h6">{t('Request body')}</Typography>
        <TextFieldWithReplacementTokens
          fullWidth
          tokens={tokens}
          placeholder={t('{...')}
          multiline
          rows={10}
          value={value.jsonPayload}
          onChange={(nextPayload) => {
            const nextValue: IAutomationActionTemplateHttpPost = {
              ...value,
              jsonPayload: nextPayload
            };
            onChange(nextValue);
          }}
        />
      </FlexContainerVertical>
    </FlexContainerVertical>
  );
};

export const ActionSettingsHttpPostReadOnly = ({
  value,
  expTpl
}: {
  value: IAutomationActionTemplateHttpPost;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const getAuthLable = () => {
    switch (value.auth.type) {
      case 'BASIC_AUTH':
        return 'Basic Authentication';
      case 'HEADER':
        return 'Athentication Header';
      case 'NO_AUTH':
      default:
        return 'None';
    }
  };
  return (
    <div>
      <FlexContainerVertical>
        <DataFieldWithLabel label={t('Request Url')}>
          {value.requestUrl}
        </DataFieldWithLabel>
        <DataFieldWithLabel label={t('Auth Type')}>
          {getAuthLable()}
        </DataFieldWithLabel>

        <DataFieldWithLabel preventOverflow label={t('Request Body')}>
          <TextField
            fullWidth
            multiline
            rows={10}
            disabled
            value={value.jsonPayload}
          />
        </DataFieldWithLabel>
      </FlexContainerVertical>
    </div>
  );
};
